import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Typography, Box, Button, Divider, Skeleton } from '@mui/material';
import ContainerBox from 'components/ContainerBox';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { Link } from 'react-router-dom';

import * as InventorySelectors from 'selectors/InventorySelectors';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as ActiveProductSelectors from 'selectors/ActiveProductSelectors';
import { clearSkuItemsListener, setSkuItemsListener } from 'actions/InventoryActions';

import useDebouncedForm from 'common/hooks/useDebouncedForm';
import { editLocalProduct } from 'actions/ActiveProductActions';
import { isEmpty } from 'lodash';

import ConnectVariantRow from './components/ConnectVariantRow';
import { VariantStockSource } from 'common/modules/inventory';
import { getVariantSkuIds } from 'common/modules/products/variants';
import { notUndefined } from 'common/utils/common';
import { Trans, useTranslation } from 'services/localization';
import { Routes } from 'routing';
import InventoryLearnMore from 'components/InventoryLearnMore';

const ConnectInventory = () => {
	const classes = useStyles();
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const product = useSelector(ActiveProductSelectors.productDataWithChanges);
	const shopId = useSelector(ShopSelectors.activeShopId)!;
	const skuItems = useSelector(InventorySelectors.skuItems);
	const isEditingDisabled = useSelector(ActiveProductSelectors.isEditingDisabled);

	const skuItemsLoading = skuItems.loading;
	const productInitiallyConnectedToInventory = !!product.variants?.options.some(
		(v) => !isEmpty(v.stockSources),
	);
	const [connectedToInventory, setConnectedToInventory] = useState<boolean>(
		productInitiallyConnectedToInventory,
	);
	const initialCollectProductCode = product.collectProductCode;

	useEffect(() => {
		dispatch(setSkuItemsListener(shopId));
		return () => {
			dispatch(clearSkuItemsListener());
		};
	}, [dispatch, shopId]);

	const { state, editState } = useDebouncedForm({
		memoizedInitialState: useMemo(
			() => ({
				variants: product.variants,
				collectProductCode: product.collectProductCode,
			}),
			[product],
		),
		memoizedOnDebounce: useCallback(
			(changes) => {
				dispatch(editLocalProduct(changes));
			},
			[dispatch],
		),
	});

	const productVariants = state.variants.options;

	const onVariantStockSourcesChange = (
		variantId: string,
		stockSources: VariantStockSource | null,
	) => {
		const newVariants = {
			...state.variants,
			options: state.variants!.options.map((o) => {
				return o.id === variantId
					? {
							...o,
							stockSources: stockSources,
					  }
					: o;
			}),
		};
		const addedSkuIds = getVariantSkuIds(newVariants.options);
		const hasSingleSku = addedSkuIds
			.map((id) => (skuItems.data ?? []).find((skuItem) => skuItem.id === id))
			.filter(notUndefined)
			.some((skuItem) => skuItem.itemType === 'single');
		editState({
			variants: newVariants,
			collectProductCode: hasSingleSku ? true : initialCollectProductCode,
		});
	};

	const multiVariant = productVariants.length > 1;

	return (
		<>
			<ContainerBox>
				<Typography variant="h6">{t('product.inventory', 'Inventory')}</Typography>
				<Typography variant="body2" color="secondary">
					<Trans
						i18nKey="product.connectProductInfo"
						defaults="Connect this product to <supportLink>inventory</supportLink>"
						components={{
							supportLink: (
								<Link to={Routes.INVENTORY} className={classes.link} target="_blank">
									{'inventory'}
								</Link>
							),
						}}
					/>
				</Typography>
				<Box mt={4} />
				{!connectedToInventory ? (
					<Button
						variant="contained"
						color="primary"
						onClick={() => setConnectedToInventory(true)}
						disabled={isEditingDisabled}
					>
						{t('product.connectToInventory', 'Connect to inventory')}
					</Button>
				) : (
					<>
						{skuItemsLoading ? (
							<Skeleton variant="rectangular" height={50} />
						) : (
							<>
								{multiVariant && (
									<Typography variant="body1" color="secondary">
										{t('product.variant', 'Variant')}
									</Typography>
								)}
								<Box mt={2}>
									{productVariants.map((v, i) => {
										const lastItem = productVariants.length - 1 === i;
										return (
											<React.Fragment key={v.id}>
												<ConnectVariantRow
													productName={product.name.def}
													variant={v}
													onStockSourcesChange={(stockSources) =>
														onVariantStockSourcesChange(v.id, stockSources)
													}
													skuItems={skuItems.data ?? []}
													styling={{
														inputSize: multiVariant ? 'small' : 'medium',
													}}
												/>
												{!lastItem && (
													<Box my={1.5}>
														<Divider />
													</Box>
												)}
											</React.Fragment>
										);
									})}
								</Box>
							</>
						)}
					</>
				)}
				<Box mt={1} />
			</ContainerBox>
			<InventoryLearnMore />
		</>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		link: {
			color: theme.palette.primary.main,
			textDecoration: 'underline',
		},
	}),
);

export default ConnectInventory;
